'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface RegionData {
  id: string;
  name: string;
  status: 'active' | 'planning' | 'future';
  investment: string;
  projects: number;
  jobs: number;
  description: string;
  coordinates: { lat: number; lng: number };
}

interface RegionalMapProps {
  className?: string;
}

const RegionalMap = ({ className = '' }: RegionalMapProps) => {
  const [selectedRegion, setSelectedRegion] = useState<string>('kenya');

  const regions: RegionData[] = [
    {
      id: 'kenya',
      name: 'Kenya (Flagship)',
      status: 'active',
      investment: 'USD 180M',
      projects: 24,
      jobs: 15000,
      description: 'Primary operations hub with established government partnerships, comprehensive infrastructure investments, and proven workforce development programs.',
      coordinates: { lat: -1.286389, lng: 36.817223 }
    },
    {
      id: 'tanzania',
      name: 'Tanzania',
      status: 'planning',
      investment: 'USD 95M',
      projects: 8,
      jobs: 6500,
      description: 'Expansion phase focusing on agricultural value chains and port infrastructure development in partnership with regional authorities.',
      coordinates: { lat: -6.792354, lng: 39.208328 }
    },
    {
      id: 'uganda',
      name: 'Uganda',
      status: 'planning',
      investment: 'USD 72M',
      projects: 6,
      jobs: 4800,
      description: 'Strategic entry into manufacturing and renewable energy sectors with government collaboration frameworks under development.',
      coordinates: { lat: 0.347596, lng: 32.582520 }
    },
    {
      id: 'rwanda',
      name: 'Rwanda',
      status: 'future',
      investment: 'USD 45M',
      projects: 4,
      jobs: 3200,
      description: 'Technology and innovation focus aligned with national digital transformation agenda, partnership discussions initiated.',
      coordinates: { lat: -1.940278, lng: 29.873888 }
    },
    {
      id: 'ethiopia',
      name: 'Ethiopia',
      status: 'future',
      investment: 'USD 120M',
      projects: 12,
      jobs: 9500,
      description: 'Large-scale infrastructure and manufacturing opportunities under evaluation, government engagement in preliminary stages.',
      coordinates: { lat: 9.145000, lng: 40.489673 }
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-success text-success-foreground';
      case 'planning':
        return 'bg-warning text-warning-foreground';
      case 'future':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active Operations';
      case 'planning':
        return 'Expansion Phase';
      case 'future':
        return 'Future Pipeline';
      default:
        return 'Under Review';
    }
  };

  const currentRegion = regions.find(r => r.id === selectedRegion) || regions[0];

  return (
    <section className={`py-4 bg-card ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mb-12">
          <h2 className="font-headline text-3xl md:text-4xl font-semibold text-foreground mb-6">
            Regional Operations & Expansion Roadmap
          </h2>
          <p className="font-body text-lg text-text-secondary leading-relaxed">
            Strategic geographic expansion built on proven Kenya flagship model, with phased implementation ensuring sustainable growth and authentic local partnerships across East Africa.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-background rounded-xl border border-border overflow-hidden shadow-md">
              <div className="aspect-video w-full">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title={currentRegion.name}
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps?q=${currentRegion.coordinates.lat},${currentRegion.coordinates.lng}&z=6&output=embed`}
                  className="border-0"
                />
              </div>
              
              <div className="p-6 border-t border-border">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-headline text-2xl font-semibold text-foreground">
                    {currentRegion.name}
                  </h3>
                  <span className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(currentRegion.status)}`}>
                    <div className="w-2 h-2 bg-current rounded-full"></div>
                    <span>{getStatusLabel(currentRegion.status)}</span>
                  </span>
                </div>
                
                <p className="font-body text-sm text-text-secondary leading-relaxed mb-6">
                  {currentRegion.description}
                </p>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-card rounded-lg p-4 border border-border">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="CurrencyDollarIcon" size={20} variant="outline" className="text-primary" />
                      <span className="font-body text-xs text-muted-foreground">Investment</span>
                    </div>
                    <div className="font-headline text-lg font-semibold text-foreground">{currentRegion.investment}</div>
                  </div>
                  <div className="bg-card rounded-lg p-4 border border-border">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="BriefcaseIcon" size={20} variant="outline" className="text-primary" />
                      <span className="font-body text-xs text-muted-foreground">Projects</span>
                    </div>
                    <div className="font-headline text-lg font-semibold text-foreground">{currentRegion.projects}</div>
                  </div>
                  <div className="bg-card rounded-lg p-4 border border-border">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="UserGroupIcon" size={20} variant="outline" className="text-primary" />
                      <span className="font-body text-xs text-muted-foreground">Jobs</span>
                    </div>
                    <div className="font-headline text-lg font-semibold text-foreground">{currentRegion.jobs.toLocaleString()}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="font-headline text-lg font-semibold text-foreground mb-4">Select Region</h3>
            {regions.map((region) => (
              <button
                key={region.id}
                onClick={() => setSelectedRegion(region.id)}
                className={`w-full text-left p-4 rounded-lg border transition-all duration-300 ${
                  selectedRegion === region.id
                    ? 'bg-primary text-primary-foreground border-primary shadow-md'
                    : 'bg-background text-foreground border-border hover:border-primary hover:shadow-sm'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-body font-medium">{region.name}</span>
                  <Icon 
                    name="ChevronRightIcon" 
                    size={20} 
                    variant="outline" 
                    className={selectedRegion === region.id ? 'text-primary-foreground' : 'text-muted-foreground'}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    selectedRegion === region.id 
                      ? 'bg-primary-foreground/20 text-primary-foreground' 
                      : getStatusColor(region.status)
                  }`}>
                    <div className="w-1.5 h-1.5 bg-current rounded-full"></div>
                    <span>{getStatusLabel(region.status)}</span>
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RegionalMap;